import type { AdminRouterContext } from '@/lib/router-context'
import type { Location } from '@/lib/services/locations/locations.types'
import { useTimezone } from '@/hooks/useTimezoneContext'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import {
  locationFilters,
  locationQueries,
  useLocation,
} from '@/lib/services/locations/locations.query'
import { locationIdSchema } from '@/lib/services/locations/locations.schema'
import { createFileRoute, notFound, Outlet } from '@tanstack/react-router'
import { useEffect } from 'react'

const filters = locationFilters().get()

export async function loadLocationDetail({
  context,
  params,
}: {
  context: AdminRouterContext
  params: { orgId: number; locationId: number }
}): Promise<{ location: Location }> {
  if (!context.ability.can('view', 'locations')) throw notFound()

  const location = await context.queryClient.ensureQueryData(
    locationQueries.show({
      organizationId: params.orgId,
      locationId: params.locationId,
      filters,
    })
  )

  return { location }
}

export const Route = createFileRoute('/_authenticated/$orgId/settings/locations/$locationId')({
  component: LocationIdLayout,
  head: adminRouteTitleHead,
  params: {
    parse: (params) => locationIdSchema.parse(params),
  },
  loader: loadLocationDetail,
})

export function LocationIdLayout() {
  const { locationId } = Route.useParams()
  const { data: location } = useLocation(locationId, { filters })
  const { pushTimezone, popTimezone } = useTimezone()

  useEffect(() => {
    const tz = location?.address?.timezone
    if (!tz) return // no clean-up function if we didn't `push`
    pushTimezone(tz)

    return () => popTimezone()
  }, [location?.address?.timezone, pushTimezone, popTimezone])
  return <Outlet />
}
